import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import { Skeleton } from "@learn-me/ui";

interface TopCoursesChartProps {
  data: Array<{
    course: { id: string; title: string; slug: string };
    revenue: number;
    sales: number;
    percentage: number;
  }>;
  isLoading?: boolean;
}

function formatCurrency(value: number): string {
  if (value >= 1000000) {
    return `$${(value / 1000000).toFixed(1)}M`;
  }
  if (value >= 1000) {
    return `$${(value / 1000).toFixed(1)}K`;
  }
  return `$${value.toFixed(0)}`;
}

function truncateTitle(title: string, maxLength = 30): string {
  if (title.length <= maxLength) return title;
  return title.slice(0, maxLength) + "...";
}

const COLORS = [
  "hsl(var(--primary))",
  "hsl(var(--chart-2))",
  "hsl(var(--chart-3))",
  "hsl(var(--chart-4))",
  "hsl(var(--chart-5))",
];

export function TopCoursesChart({ data, isLoading }: TopCoursesChartProps) {
  if (isLoading) {
    return <Skeleton className="h-[300px] w-full" />;
  }

  if (!data || data.length === 0) {
    return (
      <div className="h-[300px] flex items-center justify-center text-muted-foreground">
        No hay datos de cursos para mostrar
      </div>
    );
  }

  const chartData = data.slice(0, 10).map((item, index) => ({
    name: truncateTitle(item.course.title),
    fullName: item.course.title,
    revenue: item.revenue,
    sales: item.sales,
    percentage: item.percentage,
    colorIndex: index,
  }));

  return (
    <ResponsiveContainer
      width="100%"
      height={Math.max(300, chartData.length * 50)}
    >
      <BarChart
        layout="vertical"
        data={chartData}
        margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
      >
        <CartesianGrid
          strokeDasharray="3 3"
          className="stroke-muted"
          horizontal={false}
        />
        <XAxis
          type="number"
          tickFormatter={formatCurrency}
          tick={{ fontSize: 12 }}
          tickLine={false}
          axisLine={false}
        />
        <YAxis
          type="category"
          dataKey="name"
          width={150}
          tick={{ fontSize: 12 }}
          tickLine={false}
          axisLine={false}
        />
        <Tooltip
          contentStyle={{
            backgroundColor: "hsl(var(--popover))",
            border: "1px solid hsl(var(--border))",
            borderRadius: "8px",
          }}
          content={({ active, payload }) => {
            if (!active || !payload || payload.length === 0) return null;
            const data = payload[0].payload as {
              fullName: string;
              revenue: number;
              sales: number;
              percentage: number;
            };
            return (
              <div className="bg-popover border rounded-lg p-3 shadow-lg space-y-1">
                <div className="font-medium">{data.fullName}</div>
                <div className="text-sm">
                  Ingresos: {formatCurrency(data.revenue)}
                </div>
                <div className="text-sm">Ventas: {data.sales}</div>
                <div className="text-sm">
                  Porcentaje: {data.percentage.toFixed(1)}%
                </div>
              </div>
            );
          }}
        />
        <Bar dataKey="revenue" radius={[0, 4, 4, 0]}>
          {chartData.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}
